import { InputBase, styled } from "@mui/material";
import { HEADER_COMPACT_BREAKPOINT_PX } from "@/shared/ui/useCompactLayout";

const SEARCH_HEIGHT_PX = 36;
const SEARCH_FIELD_FONT_SIZE_PX = 10.5625;
const SEARCH_FIELD_LINE_HEIGHT_PX = 16;
/** Open-card input row. Taller than the resting toolbar field. */
const SEARCH_HEADER_HEIGHT_PX = 46;
export const SEARCH_RESULTS_MAX_HEIGHT_PX = 320;
export const SEARCH_ICON_WIDTH_PX = 13.81;
const SEARCH_EXPAND_DURATION_MS = 320;
/** Shared by the compact width grow and the open-card morph. */
export const SEARCH_MORPH_DURATION_MS = SEARCH_EXPAND_DURATION_MS;
/** At this width and below, the active field fills the toolbar inside its padding. */
export const SEARCH_ACTIVE_FULL_BLEED_MAX_PX = 740;
/** Active field cap on viewports wider than {@link SEARCH_ACTIVE_FULL_BLEED_MAX_PX}. */
export const SEARCH_ACTIVE_MAX_WIDTH_PX = 680;
export const SEARCH_PANEL_GUTTER_PX = 12;
/** Blur applied to the page behind the open search field. */
export const SEARCH_BACKDROP_BLUR = "blur(8px)";
const SEARCH_BORDER_PX = 2;
/**
 * Resting toolbar field on every screen. Content sizes the box inside this
 * band: a short query stays at the floor, the placeholder and a long query
 * stop at the cap. Focus still lifts the field into the centered card.
 */
export const SEARCH_RESTING_MIN_WIDTH_PX = 200;
export const SEARCH_RESTING_MAX_WIDTH_PX = 280;

/** Icon, padding, gap, and border around the typed query, plus a little slack so the last glyph is not clipped. */
export function headerSearchFittedWidthPx(textWidthPx: number): number {
  return Math.ceil(
    textWidthPx
    + SEARCH_LEFT_PAD_PX
    + SEARCH_ICON_WIDTH_PX
    + SEARCH_ICON_INPUT_GAP_PX
    + SEARCH_RIGHT_PAD_PX
    + SEARCH_BORDER_PX
    + 4,
  );
}
export const SEARCH_ICON_HEIGHT_PX = 16;
export const SEARCH_LEFT_PAD_PX = 14;
const SEARCH_RIGHT_PAD_PX = 8;
export const SEARCH_ICON_INPUT_GAP_PX = 8;

/** Reserves the focused width so the search expansion doesn't shift the flex layout. */
export const SearchSizer = styled("div")(({ theme }) => {
  const s = theme.app.headerSearch;
  return {
    flexShrink: 0,
    display: "flex",
    justifyContent: "flex-end",
    [theme.breakpoints.up("sm")]: {
      width: `${s.widthFocusedPxSm}px`,
    },
    // Below the header collapse the field fills whatever the toolbar has left.
    [theme.breakpoints.down(HEADER_COMPACT_BREAKPOINT_PX)]: {
      flex: "1 1 auto",
      width: "auto",
      minWidth: 0,
      maxWidth: "100%",
    },
    // Desktop header is on screen, but a reserved slot covers the nav.
    // Use only the free width, capped at the large-screen focused size.
    [theme.breakpoints.between(HEADER_COMPACT_BREAKPOINT_PX, "lg")]: {
      flex: "1 1 auto",
      width: "auto",
      minWidth: `${SEARCH_RESTING_MIN_WIDTH_PX}px`,
      maxWidth: `${s.widthPxSm}px`,
      // The field leaves the sizer while the card is open, so :focus-within drops.
      "&:focus-within, &[data-search-open]": {
        maxWidth: `${s.widthFocusedPxSm}px`,
      },
    },
  };
});

export const Search = styled("div", {
  shouldForwardProp: (prop) => prop !== "overlay" && prop !== "withResults",
})<{ overlay?: boolean; withResults?: boolean }>(({ theme, overlay, withResults }) => {
  const s = theme.app.headerSearch;
  const duration = s.transitionDurationMs;
  if (overlay) {
    return {
      boxSizing: "border-box",
      position: "fixed",
      zIndex: theme.zIndex.modal + 1,
      display: "flex",
      flexDirection: "column",
      isolation: "isolate",
      overflow: "hidden",
      borderRadius: withResults ? 10 : 8,
      border: `1px solid ${s.activeColor}`,
      backgroundColor: withResults ? s.cardBackground : s.backgroundColor,
      fontFamily: theme.typography.fontFamily,
      boxShadow: withResults ? theme.shadows[8] : theme.shadows[4],
      height: withResults ? "auto" : SEARCH_HEIGHT_PX,
      "&:focus-within .MuiSvgIcon-root": {
        color: s.activeColor,
      },
      minWidth: 0,
      transition: theme.transitions.create(["left", "width"], {
        duration: SEARCH_MORPH_DURATION_MS,
        easing: theme.transitions.easing.easeInOut,
      }),
      "@media (prefers-reduced-motion: reduce)": {
        transition: "none",
      },
    };
  }
  return {
    boxSizing: "border-box",
    position: "relative",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    padding: `0 ${SEARCH_RIGHT_PAD_PX}px 0 ${SEARCH_LEFT_PAD_PX}px`,
    isolation: "isolate",
    borderRadius: 8,
    border: `1px solid ${s.borderColor}`,
    backgroundColor: s.backgroundColor,
    height: SEARCH_HEIGHT_PX,
    width: `${SEARCH_RESTING_MAX_WIDTH_PX}px`,
    minWidth: `min(100%, ${SEARCH_RESTING_MIN_WIDTH_PX}px)`,
    maxWidth: `min(100%, ${SEARCH_RESTING_MAX_WIDTH_PX}px)`,
    transition: theme.transitions.create("width", {
      duration,
      easing: "ease-in-out",
    }),
    "&:hover": {
      borderColor: s.iconAndTextColor,
    },
    "&:focus-within .MuiSvgIcon-root": {
      color: s.activeColor,
    },
    // Stay inside the sizer so the field cannot paint over the nav or account control.
    [theme.breakpoints.between(HEADER_COMPACT_BREAKPOINT_PX, "lg")]: {
      overflow: "hidden",
      width: "100%",
    },
    [theme.breakpoints.down(HEADER_COMPACT_BREAKPOINT_PX)]: {
      overflow: "hidden",
      width: "100%",
      transition: theme.transitions.create("width", {
        duration: SEARCH_EXPAND_DURATION_MS,
        easing: theme.transitions.easing.easeInOut,
      }),
      "@media (prefers-reduced-motion: reduce)": {
        transition: "none",
      },
    },
  };
});

/** Input row. Padding lives on `Search` until the field becomes the card header. */
export const SearchInputRow = styled("div", {
  shouldForwardProp: (prop) => prop !== "overlay" && prop !== "withResults",
})<{ overlay?: boolean; withResults?: boolean }>(({ theme, overlay, withResults }) => {
  const s = theme.app.headerSearch;
  const cardHeader = Boolean(overlay && withResults);
  const row = {
    display: "flex",
    flexDirection: "row" as const,
    alignItems: "center",
    gap: cardHeader ? "10px" : `${SEARCH_ICON_INPUT_GAP_PX}px`,
    boxSizing: "border-box" as const,
    minWidth: 0,
    flex: overlay ? "0 0 auto" : "1 1 auto",
    height: cardHeader ? SEARCH_HEADER_HEIGHT_PX : "100%",
  };
  if (cardHeader) {
    return {
      ...row,
      padding: "0 14px",
      backgroundColor: s.cardHeaderBackground,
      borderBottom: `1px solid ${s.activeColor}`,
    };
  }
  if (overlay) {
    return {
      ...row,
      padding: `0 ${SEARCH_RIGHT_PAD_PX}px 0 ${SEARCH_LEFT_PAD_PX}px`,
    };
  }
  return row;
});

/** Collapses the result list so the card can grow from the field height. */
export const SearchResultsClip = styled("div", {
  shouldForwardProp: (prop) => prop !== "expanded",
})<{ expanded?: boolean }>(({ theme, expanded }) => ({
  display: "grid",
  gridTemplateRows: expanded ? "1fr" : "0fr",
  minHeight: 0,
  transition: theme.transitions.create("grid-template-rows", {
    duration: SEARCH_MORPH_DURATION_MS,
    easing: theme.transitions.easing.easeInOut,
  }),
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
  },
  "& > *": {
    overflow: "hidden",
    minHeight: 0,
  },
}));

export const SearchIconWrapper = styled("div", {
  shouldForwardProp: (prop) => prop !== "withResults",
})<{ withResults?: boolean }>(({ theme, withResults }) => {
  const s = theme.app.headerSearch;
  const size = SEARCH_ICON_HEIGHT_PX;
  const width = withResults ? 16 : SEARCH_ICON_WIDTH_PX;
  return {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    zIndex: 0,
    "& .MuiSvgIcon-root": {
      boxSizing: "border-box",
      display: "block",
      flexShrink: 0,
      fontSize: `${size}px`,
      width: `${width}px`,
      height: `${size}px`,
      color: withResults ? s.cardPlaceholderColor : s.placeholderColor,
    },
  };
});

export const StyledInputBase = styled(InputBase)(({ theme }) => {
  const s = theme.app.headerSearch;
  const text = s.filledTextColor;
  return {
    caretColor: s.activeColor,
    flex: 1,
    minWidth: 0,
    zIndex: 1,
    fontFamily: "var(--font-sans)",
    fontStyle: "normal",
    fontWeight: 400,
    fontSize: `${SEARCH_FIELD_FONT_SIZE_PX}px`,
    lineHeight: `${SEARCH_FIELD_LINE_HEIGHT_PX}px`,
    "&& .MuiInputBase-input": {
      boxSizing: "border-box",
      padding: 0,
      width: "100%",
      fontFamily: "inherit",
      fontStyle: "inherit",
      fontWeight: "inherit",
      fontSize: "inherit",
      lineHeight: "inherit",
      color: text,
      WebkitTextFillColor: text,
      textOverflow: "ellipsis",
      "&::placeholder": {
        fontFamily: "inherit",
        fontStyle: "inherit",
        fontWeight: "inherit",
        fontSize: "inherit",
        lineHeight: "inherit",
        color: s.restingPlaceholderColor,
        WebkitTextFillColor: s.restingPlaceholderColor,
        opacity: 1,
      },
    },
  };
});
